import { FieldList } from '../components/hideshow-fields-param';

export interface SaleOrderExportColumn {
    key: string;
    header: string;
}

export interface BuildSaleOrderExportColumnsOptions {
    displayedColumns: string[];
    fieldList: FieldList[] | Record<string, boolean>;
    isAdmin: boolean;
    isDelegateAdmin: boolean;
    isB2B: boolean;
}

const NON_EXPORTABLE_COLUMNS = new Set([
    'checkboxes',
    'downloadParcelLabel',
    'donwloadDocuments',
    'returns',
    'uploads',
    'linkToViewUpload',
    'syncAftershipRefresh',
    'eciSellOrderTotalTest',
    'shippingPriceTest',
    'productsTest',
]);

const HIDE_SHOW_FIELD_KEYS: Record<string, string> = {
    orderID: 'orderId',
    vatCharges: 'marketplaceVat',
};

const HIDE_SHOW_COLUMNS = new Set([
    'reorder',
    'eciOrderDate',
    'orderID',
    'orderId',
    'stripeAmount',
    'stripeRefundAmount',
    'marketplaceVat',
    'vatCharges',
    'conversionRate',
    'empactVatAmountEur',
    'empactTotalOrderAmountEur',
    'eciVat',
]);

const COLUMN_HEADERS_B2C: Record<string, string> = {
    operationalStatus: 'Operational Status',
    financialStatus: 'Financial Status',
    parcelCount: 'Parcel Count',
    reorder: 'Reorder',
    eciOrderDate: 'Empact Order Date',
    orderDate: 'Order Date',
    originCountryName: 'Country Of Origin',
    countryName: 'Country Of Destination',
    customerName: 'Customer Name',
    shopId: 'Shop ID',
    shopName: 'Shop Name',
    orderID: 'Order ID',
    morderID: 'Marketplace Order ID',
    batchId: 'Batch Id',
    consolNumber: 'Consol Number',
    regularConsolNumber: 'Consol Number',
    batchOriginCountryName: 'Batch Country Of Origin',
    batchDestinationCountryName: 'Batch Country Of Destination',
    batchType: 'Batch Type',
    fileName: 'File Name',
    syncAftership: 'Sync with AfterShip',
    shipmentId: 'Shipment ID',
    carrier: 'Carrier',
    trackingNumber: 'Tracking Number',
    orderCurrency: 'Currency',
    stripeAmount: 'Stripe Amount',
    stripeRefundAmount: 'Stripe Refund Amount',
    conversionRate: 'Conversion Rate',
    eciVat: 'Amount Exc.VAT',
    totalWithoutVat: 'VAT Amount',
    vatCharges: 'Market Place VAT',
    empactVatAmountEur: 'Empact VAT Amount (EUR)',
    empactTotalOrderAmountEur: 'Empact Total Order Amount (EUR)',
    orderAmount: 'Total Order Amount',
    marketPlace: 'Market Place',
    deliveryDate: 'Delivery Date',
    batchErrors: 'Errors',
    confirmStatus: 'Sync Status',
};

const COLUMN_HEADERS_B2B: Record<string, string> = {
    ...COLUMN_HEADERS_B2C,
    morderID: 'PO number',
    impShipment: 'Imp Shipment',
    buyerName: 'Buyer name',
    paymentTerms: 'Payment Terms',
};

function normalizeFieldList(
    fieldList: FieldList[] | Record<string, boolean>,
): Record<string, boolean> {
    if (Array.isArray(fieldList)) {
        const out: Record<string, boolean> = {};
        for (const field of fieldList) {
            if (field?.key != null) {
                out[field.key] = !!field.value;
            }
        }
        return out;
    }
    return { ...fieldList };
}

function isColumnVisible(
    columnKey: string,
    fields: Record<string, boolean>,
    isAdmin: boolean,
    isDelegateAdmin: boolean,
): boolean {
    if (columnKey === 'syncAftership') {
        return isAdmin;
    }

    const fieldKey = HIDE_SHOW_FIELD_KEYS[columnKey] || columnKey;
    if (!HIDE_SHOW_COLUMNS.has(columnKey) && !HIDE_SHOW_COLUMNS.has(fieldKey)) {
        return true;
    }

    if (columnKey === 'reorder') {
        if (isAdmin || isDelegateAdmin) {
            return !!fields.reorder;
        }
        return true;
    }

    return !!fields[fieldKey];
}

/** Merge saved Hide/Show settings (localStorage) over page defaults so export matches the table. */
export function resolveEffectiveFieldListForExport(
    fieldList: FieldList[] | Record<string, boolean>,
    settingName: string,
    localStorageSvc: { get(key: string): unknown },
): Record<string, boolean> {
    const fields = normalizeFieldList(fieldList);
    const stored = localStorageSvc.get(settingName);
    if (Array.isArray(stored)) {
        for (const field of stored) {
            if (field?.key != null) {
                fields[field.key] = !!field.value;
            }
        }
    }
    return fields;
}

export function buildSaleOrderExportColumns(
    options: BuildSaleOrderExportColumnsOptions,
): SaleOrderExportColumn[] {
    const fields = normalizeFieldList(options.fieldList);
    const headers = options.isB2B ? COLUMN_HEADERS_B2B : COLUMN_HEADERS_B2C;
    const result: SaleOrderExportColumn[] = [];

    for (const key of options.displayedColumns) {
        if (NON_EXPORTABLE_COLUMNS.has(key)) {
            continue;
        }
        if (!isColumnVisible(key, fields, options.isAdmin, options.isDelegateAdmin)) {
            continue;
        }
        const header = headers[key];
        if (!header) {
            continue;
        }
        result.push({ key, header });
    }

    return result;
}
